/**
 * serverTime — Wall-clock alignment between client devices and the Firestore
 * server, so countdowns, tick beeps, and end horns fire at the same moment
 * on every TV / iPad / laptop watching the same competition session.
 *
 * Why this exists:
 *   Every device computes `remaining = endTime - Date.now()`, and the
 *   tick-beep scheduling uses `setTimeout(fn, msUntilEnd - n*1000)`. If two
 *   TVs' clocks differ by 800 ms, their 5-4-3-2-1 beeps fire 800 ms apart
 *   and the audience hears desynced sound. Modern devices NTP-sync, but a
 *   long-backgrounded browser, a misconfigured laptop, or an old Android TV
 *   can drift by several seconds — easily noticed in a live competition.
 *
 * Algorithm (NTP-lite):
 *   1. `t0 = Date.now()`
 *   2. Write `users/{uid}._clockPing = serverTimestamp()`
 *   3. Read the doc back → `serverMs = data._clockPing.toMillis()`
 *   4. `t1 = Date.now()`
 *   5. The server stamped the doc somewhere between `t0` and the moment the
 *      read came back. Best estimator: `t0 + rtt/2` where `rtt = t1 - t0`.
 *      Therefore `offset = serverMs - (t0 + rtt/2)`.
 *
 * Cost: one user-doc update + one user-doc read per sync. The update fires
 * the `syncMemberDisplayName` CF, which short-circuits when `name`/`email`
 * are unchanged — a no-op invocation per sync, fractions of a cent.
 *
 * Public API:
 *   serverNow()                — current wall-clock as the server sees it
 *   getServerOffset()          — last-known offset in ms (0 if unresolved)
 *   isServerTimeResolved()     — whether at least one sync has succeeded
 *   syncServerTime()           — kick a sync now (deduped if one is in flight)
 *   subscribeServerOffset(cb)  — listen for offset changes
 *   useServerOffset()          — React hook returning the current offset
 *   installServerTimeSync()    — wire up boot / online / visibility / 5-min
 *                                interval auto-sync. Call once at app boot.
 */

import { useEffect, useState } from "react";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { withTimeout } from "./promiseTimeout";
import { sessionStore } from "./safeStorage";

const SS_KEY = "sendly.serverOffsetMs";
const SS_RESOLVED_KEY = "sendly.serverOffsetResolvedAt";
const RESYNC_INTERVAL_MS = 5 * 60 * 1000;       // 5 minutes
const RESYNC_VISIBILITY_THRESHOLD_MS = 60 * 1000; // resync if hidden > 1 min
// Hard timeout on the round-trip. Without offline persistence (memory-only
// Firestore — current iPad-safe config) `setDoc()` / `getDoc()` stay
// pending forever when the network is down — there's no built-in SDK
// timeout. Without this guard, an offline sync attempt would lock
// `_inFlight` permanently and every subsequent `syncServerTime()` call
// would hand back the same un-resolving promise.
const SYNC_TIMEOUT_MS = 5000;

let _offset = 0;
let _resolved = false;
let _lastSyncAt = 0;
let _inFlight = null;
const _subs = new Set();

// Restore any previous offset from sessionStorage so the very first
// countdown after a tab refresh doesn't start from offset=0.
{
  const cached = sessionStore.get(SS_KEY);
  if (cached !== null) {
    const n = parseInt(cached, 10);
    if (Number.isFinite(n)) {
      _offset = n;
      const ts = parseInt(sessionStore.get(SS_RESOLVED_KEY) || "0", 10);
      if (Number.isFinite(ts) && ts > 0) {
        _resolved = true;
        _lastSyncAt = ts;
      }
    }
  }
}

function _setOffset(newOffset) {
  // Round to whole ms — sub-ms precision is noise here, and keeping the
  // value stable across no-op resyncs avoids spamming subscribers.
  const v = Math.round(newOffset);
  if (v === _offset && _resolved) return;
  _offset = v;
  _resolved = true;
  _lastSyncAt = Date.now();
  sessionStore.set(SS_KEY, String(v));
  sessionStore.set(SS_RESOLVED_KEY, String(_lastSyncAt));
  for (const cb of _subs) {
    try { cb(v); } catch { /* isolate */ }
  }
}

export function serverNow() {
  return Date.now() + _offset;
}

export function getServerOffset() {
  return _offset;
}

export function isServerTimeResolved() {
  return _resolved;
}

export function subscribeServerOffset(cb) {
  _subs.add(cb);
  return () => { _subs.delete(cb); };
}

/**
 * React hook returning the current server-time offset. Re-renders the
 * subscribing component when a new offset is measured. Components using
 * this in a `useEffect` dep array will re-run their scheduling logic so
 * `setTimeout`s and counters realign as soon as the offset resolves.
 */
export function useServerOffset() {
  const [v, setV] = useState(_offset);
  useEffect(() => {
    // Read latest in case the offset changed between render and effect.
    setV(_offset);
    return subscribeServerOffset(setV);
  }, []);
  return v;
}

/**
 * Perform one NTP-lite sync. Returns the new offset in ms.
 * Concurrent calls are deduped — a single in-flight promise is shared.
 *
 * The write targets a per-user SUBCOLLECTION doc
 * (`users/{uid}/_meta/clockPing`), NOT a field on the parent user doc.
 * That distinction matters: the App shell's consent listener
 * (`onSnapshot` on `users/{uid}`) only fires for parent-doc writes, so
 * the pending write here never makes the parent snapshot appear to lose
 * the `legalConsent` field — which is exactly the regression that
 * disabled the previous implementation (commit dcd8f95). Firestore rule
 * `users/{userId}/_meta/{docId}` allows self-read/write.
 *
 * Silently no-ops when there's no auth user. The cached offset persists;
 * we'll retry on the next trigger.
 */
export function syncServerTime() {
  if (_inFlight) return _inFlight;
  const user = auth.currentUser;
  if (!user) return Promise.resolve(_offset);

  // If the browser already knows it's offline, don't even try — saves
  // burning a guaranteed-to-time-out round-trip.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return Promise.resolve(_offset);
  }

  _inFlight = (async () => {
    try {
      const ref = doc(db, "users", user.uid, "_meta", "clockPing");
      const t0 = Date.now();
      await withTimeout(setDoc(ref, { clockPing: serverTimestamp() }), SYNC_TIMEOUT_MS, "clockPing setDoc");
      const snap = await withTimeout(getDoc(ref), SYNC_TIMEOUT_MS, "clockPing getDoc");
      const t1 = Date.now();
      const ts = snap.data()?.clockPing;
      if (!ts || typeof ts.toMillis !== "function") return _offset;
      const serverMs = ts.toMillis();
      const rtt = t1 - t0;
      // Best estimator: the server stamped the doc at ~(t0 + rtt/2) in
      // client wall-clock terms. Offset = serverMs - that midpoint.
      const offset = serverMs - (t0 + rtt / 2);
      _setOffset(offset);
      return _offset;
    } catch {
      // Permission denied (very rare — rule covers self), network error,
      // doc-not-found, etc. Keep the previously-cached offset and try
      // again on the next trigger.
      return _offset;
    } finally {
      _inFlight = null;
    }
  })();

  return _inFlight;
}

/**
 * Feed an offset measurement taken from a round-trip somebody else already
 * paid for, instead of `syncServerTime()`'s own write+read.
 *
 * This exists for the TV dashboards. They are the surface where a drifting
 * clock is most visible - a wall display counting a competition session down
 * next to a judge's iPad - and they are the one surface that can NEVER run
 * `syncServerTime()`: no user is signed in on a TV, so the `users/{uid}` ping
 * doc is unwritable. What a TV does have is its 60-second `lastSeen`
 * heartbeat, a `serverTimestamp()` write on a doc it already listens to. That
 * is the same measurement, free.
 *
 * @param {number} t0       Date.now() immediately before the write was issued
 * @param {number} t1       Date.now() when the server-resolved value arrived
 * @param {number} serverMs the server timestamp, in epoch ms
 */
export function reportClockPing(t0, t1, serverMs) {
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || !Number.isFinite(serverMs)) return;
  const rtt = t1 - t0;
  // A round-trip this long says more about the network than about the clock;
  // the midpoint estimator's error grows with it. Better to keep the previous
  // offset than to poison it.
  if (rtt < 0 || rtt > SYNC_TIMEOUT_MS) return;
  _setOffset(serverMs - (t0 + rtt / 2));
}

let _installed = false;
let _interval = null;
let _hiddenAt = 0;

/**
 * Install auto-sync wiring. Idempotent.
 * Returns a cleanup function (mostly for tests).
 */
export function installServerTimeSync() {
  if (_installed) return () => {};
  _installed = true;

  if (typeof window === "undefined") return () => {};

  // Defer the first sync slightly so we don't compete with the initial
  // Firestore listener attach burst at boot. Once auth lands, this fires.
  // If `auth.currentUser` is null at the deferred moment, syncServerTime()
  // is a no-op; the `onAuthStateChanged` listener below picks it up.
  const bootTimer = setTimeout(() => { syncServerTime().catch(() => {}); }, 2500);

  const onAuth = (u) => { if (u) syncServerTime().catch(() => {}); };
  const unsubAuth = auth.onAuthStateChanged ? auth.onAuthStateChanged(onAuth) : () => {};

  _interval = setInterval(() => {
    if (Date.now() - _lastSyncAt > RESYNC_INTERVAL_MS) {
      syncServerTime().catch(() => {});
    }
  }, 60 * 1000);

  const onOnline = () => { syncServerTime().catch(() => {}); };
  const onVis = () => {
    if (document.hidden) {
      _hiddenAt = Date.now();
      return;
    }
    if (_hiddenAt > 0 && Date.now() - _hiddenAt > RESYNC_VISIBILITY_THRESHOLD_MS) {
      syncServerTime().catch(() => {});
    }
    _hiddenAt = 0;
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVis);

  return () => {
    clearTimeout(bootTimer);
    if (_interval) clearInterval(_interval);
    if (typeof unsubAuth === "function") unsubAuth();
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVis);
    _installed = false;
  };
}

// Test helpers
/** @internal */
export function __resetServerTimeForTests() {
  _offset = 0;
  _resolved = false;
  _lastSyncAt = 0;
  _inFlight = null;
  _subs.clear();
  _installed = false;
  if (_interval) { clearInterval(_interval); _interval = null; }
  sessionStore.remove(SS_KEY);
  sessionStore.remove(SS_RESOLVED_KEY);
}
/** @internal */
export function __setOffsetForTests(v) { _setOffset(v); }
